import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import ReactRouterPrompt from 'react-router-prompt';

import {
  AddBox,
  ContentCopy,
  DeleteOutlineRounded,
  EditOutlined,
  History,
  Restore,
} from '@mui/icons-material';
import ArrowLeftIcon from '@mui/icons-material/ArrowLeft';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CodeIcon from '@mui/icons-material/Code';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';

import { Config, FooterConfig, NavigationConfig } from '../App';
import { ThemeEditor } from '../components/admin/theme-editor';
import FileSelectorModal from '../components/FileSelectorModal';
import PageEditor from '../components/PageEditor';
import { handleUpload, useConfig } from '../ConfigContext';
import { PageConfig } from '../pages/types';
import { cleanComponentData } from '../utils/component-utils';
import {
  saveConfigVersion,
  getConfigVersions,
  loadConfigVersion,
  ConfigVersion,
} from '../utils/version-control';

const normalizeConfig = (config: Config) => {
  if (!config || !config.pages) return config;

  const normalizedPages: Record<string, PageConfig> = {};

  Object.entries(config.pages).forEach(([pageKey, pageData]) => {
    if (pageData && pageData.components) {
      normalizedPages[pageKey] = {
        ...pageData,
        components: pageData.components.map((comp) => cleanComponentData(comp)),
      };
    } else {
      normalizedPages[pageKey] = pageData;
    }
  });

  return {
    ...config,
    pages: normalizedPages,
  };
};

const AdminPage = () => {
  const config = useConfig().config;
  const [configParam, setConfigParam] = useState<string | null>('anundi');

  if (!config) {
    return null;
  }
  const [currentConfig, setCurrentConfig] = useState<Config>(config);

  const normalizedConfig = normalizeConfig(currentConfig);

  const [activePage, setActivePage] = useState<string>(
    Object.keys(normalizedConfig.pages)[0] || '' // Set initial active page safely
  );
  const [newPageName, setNewPageName] = useState('');
  const [isAddPageDialogOpen, setIsAddPageDialogOpen] = useState(false);
  const [isNavDialogOpen, setIsNavDialogOpen] = useState(false);
  const [isFooterDialogOpen, setIsFooterDialogOpen] = useState(false);

  const [isThemeDialogOpen, setIsThemeDialogOpen] = useState(false);
  const [isLogoFileSelectorOpen, setIsLogoFileSelectorOpen] = useState(false);

  const [isDirty, setIsDirty] = useState(false);

  const [isRenamePageDialogOpen, setIsRenamePageDialogOpen] = useState(false);
  const [newPageKey, setNewPageKey] = useState('');

  const [isJsonEditorOpen, setIsJsonEditorOpen] = useState(false);
  const [jsonContent, setJsonContent] = useState('');
  const [jsonError, setJsonError] = useState('');

  const [tokenModalOpen, setTokenModalOpen] = useState(false);
  const [authToken, setAuthToken] = useState('');

  const [activePagePath, setActivePagePath] = useState<string[]>([]);
  const [isAddSubpageDialogOpen, setIsAddSubpageDialogOpen] = useState(false);
  const [newSubpageName, setNewSubpageName] = useState('');

  const [isRenamingSubpage, setIsRenamingSubpage] = useState(false);
  const [subpageToRename, setSubpageToRename] = useState('');

  // Version control state
  const [configVersions, setConfigVersions] = useState<ConfigVersion[]>([]);
  const [selectedVersionId, setSelectedVersionId] = useState<string>('');
  const [isVersionDialogOpen, setIsVersionDialogOpen] = useState(false);

  useEffect(() => {
    setIsDirty(true);
  }, [currentConfig]);

  // Load saved versions on component mount
  useEffect(() => {
    const versions = getConfigVersions();
    setConfigVersions(versions);
  }, []);

  // Get the active page based on the current path
  const getActivePage = (): { page: PageConfig; path: string } => {
    // Check if activePage exists in pages, otherwise use the first available page
    const currentPageKey = normalizedConfig.pages[activePage]
      ? activePage
      : Object.keys(normalizedConfig.pages)[0] || '';

    let currentPage = normalizedConfig.pages[currentPageKey];
    let fullPath = currentPageKey;

    // Safety check
    if (!currentPage) {
      return {
        page: { menutitle: '', components: [] },
        path: '',
      };
    }

    if (activePagePath.length > 0 && currentPage.subpages) {
      let tempPage = currentPage;
      let path = currentPageKey;

      for (const subpageName of activePagePath) {
        if (tempPage.subpages && tempPage.subpages[subpageName]) {
          tempPage = tempPage.subpages[subpageName];
          path += `/${subpageName}`;
        } else {
          break;
        }
      }

      currentPage = tempPage;
      fullPath = path;
    }

    return { page: currentPage, path: fullPath };
  };

  const handlePageChange = (event: React.SyntheticEvent, newValue: string) => {
    setActivePage(newValue);
    setActivePagePath([]);
  };

  const dublicatePage = (pageKey: string) => {
    setCurrentConfig((prevConfig) => {
      const newConfig = { ...prevConfig };

      // If we're duplicating a subpage
      if (activePagePath.length > 0) {
        // Get the current subpage name (last item in the path)
        const currentSubpageName = activePagePath[activePagePath.length - 1];

        // Start navigation from the main page
        let targetPage = newConfig.pages[activePage];
        let path = [...activePagePath];
        path.pop(); // Remove the last element as we need to find its parent

        // Navigate to the parent page that contains the subpage
        for (const subpageName of path) {
          if (targetPage.subpages && targetPage.subpages[subpageName]) {
            targetPage = targetPage.subpages[subpageName];
          }
        }

        if (!targetPage.subpages) {
          return prevConfig; // Safety check
        }

        // Create a new unique key for the duplicated subpage
        const newSubpageKey = `${currentSubpageName}_copy_${Date.now()}`;

        // Duplicate the subpage
        targetPage.subpages[newSubpageKey] = JSON.parse(
          JSON.stringify(targetPage.subpages[currentSubpageName])
        );

        // Update the title to indicate it's a copy
        if (targetPage.subpages[newSubpageKey].menutitle) {
          targetPage.subpages[
            newSubpageKey
          ].menutitle = `${targetPage.subpages[newSubpageKey].menutitle} (copy)`;
        }

        return newConfig;
      }

      // Original code for duplicating main pages
      const pageToDuplicate = newConfig.pages[pageKey];

      if (!pageToDuplicate) return prevConfig; // Safety check

      // Create a new unique key for the duplicated page
      const newPageKey = `${pageKey}_copy_${Date.now()}`;

      // Duplicate the page data
      newConfig.pages[newPageKey] = {
        ...pageToDuplicate,
        menutitle: `${pageToDuplicate.menutitle} (copy)`,
      };

      return newConfig;
    });
  };

  const handleSubpageClick = (subpageName: string) => {
    setActivePagePath([...activePagePath, subpageName]);
  };

  const handleGoBackToParent = () => {
    if (activePagePath.length > 0) {
      setActivePagePath(activePagePath.slice(0, -1));
    }
  };

  const handleSave = (updatedPageData: PageConfig) => {
    setCurrentConfig((prevConfig) => {
      const newConfig = { ...prevConfig };

      // If we're editing the main page
      if (activePagePath.length === 0) {
        newConfig.pages = {
          ...prevConfig.pages,
          [activePage]: updatedPageData,
        };
      }
      // If we're editing a subpage
      else {
        const pagesCopy = { ...prevConfig.pages };
        let targetPage = pagesCopy[activePage];
        let path = [...activePagePath];

        // Navigate to the parent of the subpage we want to update
        while (path.length > 1) {
          const subpageName = path.shift();
          if (
            subpageName &&
            targetPage.subpages &&
            targetPage.subpages[subpageName]
          ) {
            targetPage = targetPage.subpages[subpageName];
          }
        }

        // Update the subpage
        const lastSubpage = path[0];
        if (lastSubpage && targetPage.subpages) {
          targetPage.subpages[lastSubpage] = updatedPageData;
        }

        newConfig.pages = pagesCopy;
      }

      return newConfig;
    });
  };

  const handleConfigSave = () => {
    saveConfig();
  };

  const saveConfig = () => {
    // Save to server
    const blob = new Blob([JSON.stringify(currentConfig, null, 2)], {
      type: 'application/json',
    });
    const filename = `${configParam}.json`;
    handleUpload(blob, filename);

    // Save version to localStorage
    saveConfigVersion(currentConfig);

    // Update versions list
    const versions = getConfigVersions();
    setConfigVersions(versions);

    setIsDirty(false);
  };

  // Version control functions
  const handleLoadVersion = () => {
    if (!selectedVersionId) return;

    const versionConfig = loadConfigVersion(selectedVersionId);
    if (versionConfig) {
      setCurrentConfig(versionConfig);
      setIsVersionDialogOpen(false);
      setSelectedVersionId('');
      setIsDirty(true);
    }
  };

  const handleSaveCurrentAsVersion = () => {
    saveConfigVersion(currentConfig);
    const versions = getConfigVersions();
    setConfigVersions(versions);
  };

  useEffect(() => {
    const hostname = window.location.hostname;
    let param = hostname ? hostname : null;

    if (!param) {
      const urlParams = new URLSearchParams(window.location.search);
      param = urlParams.get('config');
    }

    if (!param) {
      param = 'anundi';
    }

    setConfigParam(param);
  }, []);

  const handleAddPage = () => {
    setCurrentConfig((prevConfig) => ({
      ...prevConfig,
      pages: {
        ...prevConfig.pages,
        [newPageName]: { menutitle: newPageName, components: [] },
      },
    }));
    setNewPageName('');
    setIsAddPageDialogOpen(false);
  };

  const handleDeletePage = () => {
    // If we're viewing a subpage (activePagePath has items), delete the subpage
    if (activePagePath.length > 0) {
      setCurrentConfig((prevConfig) => {
        const newConfig = { ...prevConfig };
        const lastSubpageName = activePagePath[activePagePath.length - 1];

        // Navigate to the parent of the subpage we want to delete
        let parentPage = newConfig.pages[activePage];
        let path = [...activePagePath];
        path.pop(); // Remove the last element which is the current subpage

        // Traverse down to find the parent containing the subpage to delete
        for (const subpageName of path) {
          if (parentPage.subpages && parentPage.subpages[subpageName]) {
            parentPage = parentPage.subpages[subpageName];
          }
        }

        // Delete the subpage
        if (parentPage.subpages) {
          delete parentPage.subpages[lastSubpageName];

          // If parent has no more subpages, clean up the empty subpages object
          if (Object.keys(parentPage.subpages).length === 0) {
            delete parentPage.subpages;
          }
        }

        return newConfig;
      });

      // Go back to parent page view
      setActivePagePath(activePagePath.slice(0, -1));
    }
    // Otherwise delete the main page
    else {
      setCurrentConfig((prevConfig) => {
        const updatedPages = { ...prevConfig.pages };
        delete updatedPages[activePage];

        // Find a new active page
        const newActivePage = Object.keys(updatedPages)[0] || '';

        // Set the new active page immediately to prevent accessing a deleted page
        setTimeout(() => setActivePage(newActivePage), 0);

        return {
          ...prevConfig,
          pages: updatedPages,
        };
      });
    }
  };

  const handleLogoFileSelect = (fileUrl: string) => {
    handleNavChange({
      ...(currentConfig.navigationConfig || {}),
      logo: {
        url: fileUrl,
        alt: currentConfig.navigationConfig?.logo?.alt || 'Logo',
        height: currentConfig.navigationConfig?.logo?.height || '40px',
      },
    });
    setIsLogoFileSelectorOpen(false);
  };

  const handleNavChange = (updatedNavConfig: NavigationConfig) => {
    setCurrentConfig((prevConfig) => ({
      ...prevConfig,
      navigationConfig: {
        ...prevConfig.navigationConfig,
        ...updatedNavConfig,
      },
    }));
  };

  const handleSaveFooter = (updatedFooterConfig: FooterConfig) => {
    setCurrentConfig((prevConfig) => ({
      ...prevConfig,
      footerConfig: updatedFooterConfig,
    }));
  };

  const handleThemeChange = (key: string, value: any) => {
    setCurrentConfig((prevConfig) => {
      const keys = key.split('.');
      const lastKey = keys.pop();
      const newTheme = { ...prevConfig.theme };

      let nestedConfig = newTheme;
      keys.forEach((k) => {
        nestedConfig[k] = { ...nestedConfig[k] };
        nestedConfig = nestedConfig[k];
      });

      if (lastKey) {
        nestedConfig[lastKey] = value;
      }

      return {
        ...prevConfig,
        theme: newTheme,
      };
    });
  };

  const handleRenamePage = () => {
    if (
      !newPageKey ||
      newPageKey === (isRenamingSubpage ? subpageToRename : activePage)
    ) {
      return;
    }

    // If we're renaming a subpage
    if (isRenamingSubpage) {
      setCurrentConfig((prevConfig) => {
        const newConfig = { ...prevConfig };

        // Start from the main page
        let parentPage = newConfig.pages[activePage];

        // For subpages displayed in the current view
        // These are either direct children of the main page or direct children of the current subpage
        if (activePagePath.length === 0) {
          // We're viewing the main page, so the subpage is a direct child
          if (!parentPage.subpages) return prevConfig;

          // Check if the subpage exists
          if (!parentPage.subpages[subpageToRename]) return prevConfig;

          // Make a copy of the subpage with the new name
          parentPage.subpages[newPageKey] = {
            ...parentPage.subpages[subpageToRename],
          };

          // Delete the original subpage
          delete parentPage.subpages[subpageToRename];
        } else {
          // We're viewing a subpage, so we need to navigate to it first
          // to find its subpages
          let currentPage = parentPage;

          // Navigate to the current viewed subpage
          for (const pathPart of activePagePath) {
            if (!currentPage.subpages || !currentPage.subpages[pathPart]) {
              return prevConfig; // Path is invalid
            }
            currentPage = currentPage.subpages[pathPart];
          }

          // Now we're at the current subpage, let's rename its child subpage
          if (!currentPage.subpages || !currentPage.subpages[subpageToRename]) {
            return prevConfig;
          }

          // Make a copy with the new name
          currentPage.subpages[newPageKey] = {
            ...currentPage.subpages[subpageToRename],
          };

          // Delete the original
          delete currentPage.subpages[subpageToRename];
        }

        return newConfig;
      });
    } else {
      // Original implementation for main page renaming
      setCurrentConfig((prevConfig) => {
        const updatedPages = { ...prevConfig.pages };
        // Copy the content from the old key to the new key
        updatedPages[newPageKey] = updatedPages[activePage];
        // Delete the old key
        delete updatedPages[activePage];

        return {
          ...prevConfig,
          pages: updatedPages,
        };
      });

      // Update active page to the new name
      setActivePage(newPageKey);
    }

    // Reset state
    setIsRenamePageDialogOpen(false);
    setIsRenamingSubpage(false);
    setSubpageToRename('');
    setNewPageKey('');
  };

  const handleMoveTab = (pageKey: string, direction: 'up' | 'down') => {
    setCurrentConfig((prevConfig) => {
      // Get ordered list of page keys
      const pageKeys = Object.keys(prevConfig.pages);
      const currentIndex = pageKeys.indexOf(pageKey);

      // Calculate new index
      const newIndex =
        direction === 'up'
          ? Math.max(0, currentIndex - 1)
          : Math.min(pageKeys.length - 1, currentIndex + 1);

      // If position doesn't change, return unchanged config
      if (newIndex === currentIndex) return prevConfig;

      // Create new ordered array of keys by swapping positions
      const newPageKeys = [...pageKeys];
      [newPageKeys[currentIndex], newPageKeys[newIndex]] = [
        newPageKeys[newIndex],
        newPageKeys[currentIndex],
      ];

      // Create new pages object with the updated order
      const orderedPages: typeof prevConfig.pages = {};
      newPageKeys.forEach((key) => {
        orderedPages[key] = prevConfig.pages[key];
      });

      // Return updated config
      return {
        ...prevConfig,
        pages: orderedPages,
      };
    });
  };

  const handleOpenJsonEditor = () => {
    setJsonContent(JSON.stringify(currentConfig, null, 2));
    setJsonError('');
    setIsJsonEditorOpen(true);
  };

  const handleSaveJson = () => {
    try {
      const parsedJson = JSON.parse(jsonContent);

      // Simple validation to check if this has the expected structure
      if (!parsedJson.pages || !parsedJson.theme) {
        throw new Error('JSON must include pages and theme properties');
      }

      setCurrentConfig(parsedJson);
      setIsJsonEditorOpen(false);
      setJsonError('');
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error ? error.message : 'Unknown error';
      setJsonError(`Invalid JSON: ${errorMessage}`);
    }
  };

  const handleAddSubpage = () => {
    if (!newSubpageName) return;

    setCurrentConfig((prevConfig) => {
      const newConfig = { ...prevConfig };

      // If we're adding to the main page
      if (activePagePath.length === 0) {
        const currentPage = { ...newConfig.pages[activePage] };
        currentPage.subpages = currentPage.subpages || {};
        currentPage.subpages[newSubpageName] = {
          menutitle: newSubpageName,
          components: [],
        };
        newConfig.pages[activePage] = currentPage;
      }
      // If we're adding to a subpage
      else {
        const pagesCopy = { ...newConfig.pages };
        let targetPage = pagesCopy[activePage];

        // Navigate to the subpage where we want to add a new subpage
        for (const subpageName of activePagePath) {
          if (targetPage.subpages && targetPage.subpages[subpageName]) {
            targetPage = targetPage.subpages[subpageName];
          }
        }

        targetPage.subpages = targetPage.subpages || {};
        targetPage.subpages[newSubpageName] = {
          menutitle: newSubpageName,
          components: [],
        };
        newConfig.pages = pagesCopy;
      }

      return newConfig;
    });

    setNewSubpageName('');
    setIsAddSubpageDialogOpen(false);
  };

  const handleMoveSubpage = (subpageName: string, direction: 'up' | 'down') => {
    setCurrentConfig((prevConfig) => {
      const newConfig = { ...prevConfig };

      // Get the current page based on active page path
      let parentPage: PageConfig;
      if (activePagePath.length === 0) {
        // If we're viewing the main page, subpages are direct children
        parentPage = { ...newConfig.pages[activePage] };

        if (!parentPage.subpages) {
          return prevConfig; // No subpages to reorder
        }

        // Get ordered list of subpage keys
        const subpageKeys = Object.keys(parentPage.subpages);
        const currentIndex = subpageKeys.indexOf(subpageName);

        // Calculate new index
        const newIndex =
          direction === 'up'
            ? Math.max(0, currentIndex - 1)
            : Math.min(subpageKeys.length - 1, currentIndex + 1);

        // If position doesn't change, return unchanged config
        if (newIndex === currentIndex) return prevConfig;

        // Create new ordered array of keys by swapping positions
        const newSubpageKeys = [...subpageKeys];
        [newSubpageKeys[currentIndex], newSubpageKeys[newIndex]] = [
          newSubpageKeys[newIndex],
          newSubpageKeys[currentIndex],
        ];

        // Create new subpages object with the updated order
        const orderedSubpages: Record<string, PageConfig> = {};
        newSubpageKeys.forEach((key) => {
          orderedSubpages[key] = parentPage.subpages![key];
        });

        // Update the parent page with the reordered subpages
        parentPage.subpages = orderedSubpages;
        newConfig.pages[activePage] = parentPage;
      } else {
        // We're viewing a nested subpage
        const pagesCopy = { ...newConfig.pages };
        let currentLevel = pagesCopy[activePage];
        const path = [...activePagePath];

        // Navigate to the subpage where we want to reorder subpages
        // (remove the last path part as that's where we currently are)
        path.pop();

        // Navigate to the correct parent level
        for (const partName of path) {
          if (currentLevel.subpages && currentLevel.subpages[partName]) {
            currentLevel = currentLevel.subpages[partName];
          } else {
            return prevConfig; // Invalid path
          }
        }

        if (!currentLevel.subpages) {
          return prevConfig; // No subpages to reorder
        }

        // Get ordered list of subpage keys
        const subpageKeys = Object.keys(currentLevel.subpages);
        const currentIndex = subpageKeys.indexOf(subpageName);

        // Calculate new index
        const newIndex =
          direction === 'up'
            ? Math.max(0, currentIndex - 1)
            : Math.min(subpageKeys.length - 1, currentIndex + 1);

        // If position doesn't change, return unchanged config
        if (newIndex === currentIndex) return prevConfig;

        // Create new ordered array of keys by swapping positions
        const newSubpageKeys = [...subpageKeys];
        [newSubpageKeys[currentIndex], newSubpageKeys[newIndex]] = [
          newSubpageKeys[newIndex],
          newSubpageKeys[currentIndex],
        ];

        // Create new subpages object with the updated order
        const orderedSubpages: Record<string, PageConfig> = {};
        newSubpageKeys.forEach((key) => {
          orderedSubpages[key] = currentLevel.subpages![key];
        });

        // Update the parent with the reordered subpages
        currentLevel.subpages = orderedSubpages;
        newConfig.pages = pagesCopy;
      }

      return newConfig;
    });
  };

  // Add this function to open rename dialog for subpages
  const handleOpenSubpageRenameDialog = (name: string) => {
    setIsRenamingSubpage(true);
    setSubpageToRename(name);
    setNewPageKey(name);
    setIsRenamePageDialogOpen(true);
  };

  if (!configParam) {
    return <div>Config not found. You need to create subdomain.</div>;
  }

  return (
    <Box
      sx={{
        paddingTop: '100px',
        maxWidth: '1400px',
        margin: '0 auto',
        backgroundColor: '#f5f5f5',
        padding: '20px',
      }}
    >
      <Button variant="contained" color="primary" component={Link} to="/">
        Back to the site
      </Button>
      <Box
        display="flex"
        justifyContent="left"
        alignItems="left"
        mb={2}
        sx={{
          padding: '10px',
          backgroundColor: '#f0f0f0',
          borderRadius: '8px',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          size="small"
          onClick={() => setIsThemeDialogOpen(true)}
          sx={{ flex: '0 1 auto' }}
        >
          Edit Theme
        </Button>
        <Button
          variant="contained"
          color="secondary"
          size="small"
          onClick={() => setIsNavDialogOpen(true)}
          sx={{ flex: '0 1 auto' }}
        >
          Edit Navigation
        </Button>
        <Button
          variant="contained"
          color="secondary"
          size="small"
          onClick={() => setIsFooterDialogOpen(true)}
          sx={{ flex: '0 1 auto' }}
        >
          Edit Footer
        </Button>
        <Button
          variant="contained"
          color="info"
          startIcon={<CodeIcon />}
          onClick={handleOpenJsonEditor}
        >
          Edit JSON
        </Button>
        <Button
          variant="contained"
          color="warning"
          startIcon={<History />}
          onClick={() => setIsVersionDialogOpen(true)}
          sx={{ flex: '0 1 auto' }}
        >
          Versions ({configVersions.length})
        </Button>
        {configVersions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Quick Restore</InputLabel>
            <Select
              value={selectedVersionId}
              onChange={(e) => setSelectedVersionId(e.target.value)}
              label="Quick Restore"
            >
              {configVersions.slice(0, 5).map((version) => (
                <MenuItem key={version.id} value={version.id}>
                  <ListItemText primary={version.name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {selectedVersionId && (
          <Button
            variant="contained"
            color="success"
            startIcon={<Restore />}
            onClick={handleLoadVersion}
            size="small"
            sx={{ flex: '0 1 auto' }}
          >
            Restore
          </Button>
        )}
      </Box>
      {/* Buttons for page actions */}
      <Box
        display="flex"
        justifyContent="left"
        alignItems="left"
        mb={2}
        sx={{
          padding: '10px',
          backgroundColor: '#f0f0f0',
          borderRadius: '8px',
          gap: 1,
          flexWrap: 'wrap',
        }}
      >
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setIsAddPageDialogOpen(true)}
          sx={{ flex: '0 1 auto' }}
        >
          <AddBox />
        </Button>
        <Button
          variant="contained"
          color="secondary"
          size="small"
          onClick={() => dublicatePage(activePage)}
          sx={{ flex: '0 1 auto' }}
        >
          <ContentCopy />
        </Button>
        <Button
          variant="contained"
          color="secondary"
          onClick={() => setIsAddSubpageDialogOpen(true)}
          sx={{ flex: '0 1 auto' }}
        >
          <AddBox />
          sub
        </Button>
      </Box>

      {/* Root page tab navigation */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box sx={{ width: '100%', mb: 3 }}>
          <Tabs
            value={activePage}
            onChange={handlePageChange}
            aria-label="page navigation tabs"
            variant="scrollable"
            scrollButtons="auto"
            textColor="primary"
            indicatorColor="primary"
          >
            {Object.keys(normalizedConfig.pages).map((pageKey) => (
              <Tab
                key={pageKey}
                value={pageKey}
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      color="primary"
                      sx={{ mr: 0.5 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveTab(pageKey, 'up');
                      }}
                    >
                      <ArrowLeftIcon fontSize="small" />
                    </IconButton>

                    {pageKey}

                    {activePage === pageKey && (
                      <Button
                        size="small"
                        color="secondary"
                        sx={{ ml: 1, minWidth: 'auto', p: '2px' }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setNewPageKey(pageKey);
                          setIsRenamePageDialogOpen(true);
                        }}
                      >
                        <EditOutlined color="primary" />
                      </Button>
                    )}

                    <IconButton
                      size="small"
                      color="primary"
                      sx={{ ml: 0.5 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleMoveTab(pageKey, 'down');
                      }}
                    >
                      <ArrowRightIcon fontSize="small" />
                    </IconButton>
                  </Box>
                }
                wrapped
                sx={{
                  color: 'black',
                  fontWeight: activePage === pageKey ? 'bold' : 'normal',
                }}
              />
            ))}
          </Tabs>
        </Box>
      </Box>

      {/* Page navigation with breadcrumb */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6">
          <Button onClick={() => setActivePagePath([])} sx={{ mx: 1 }}>
            {activePage}
          </Button>
          {activePagePath.map((subpage, index) => (
            <React.Fragment key={index}>
              {' > '}
              <Button
                onClick={() =>
                  setActivePagePath(activePagePath.slice(0, index + 1))
                }
                sx={{ mx: 1 }}
              >
                {subpage}
              </Button>
            </React.Fragment>
          ))}
        </Typography>
      </Box>

      {/* Display subpages if available */}
      {getActivePage().page.subpages &&
        Object.keys(getActivePage().page.subpages || {}).length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {Object.entries(getActivePage().page.subpages || {}).map(
                ([subpageName]) => (
                  <Box
                    key={subpageName}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      m: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      padding: '4px',
                      backgroundColor: '#f8f8f8',
                    }}
                  >
                    <Box sx={{ display: 'flex', flexDirection: 'row', mr: 1 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleMoveSubpage(subpageName, 'up')}
                      >
                        <ArrowLeftIcon />
                      </IconButton>
                    </Box>
                    <Button
                      variant="text"
                      onClick={() => handleSubpageClick(subpageName)}
                    >
                      {subpageName}
                    </Button>
                    <IconButton
                      size="small"
                      color="secondary"
                      onClick={() => handleOpenSubpageRenameDialog(subpageName)}
                      sx={{ mx: 0.5 }}
                    >
                      <EditOutlined color="primary" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => handleMoveSubpage(subpageName, 'down')}
                    >
                      <ArrowRightIcon />
                    </IconButton>
                  </Box>
                )
              )}
            </Box>
          </Box>
        )}

      {/* Page Editor */}
      <PageEditor
        pageData={getActivePage().page}
        onSave={handleSave}
        handleUpload={handleUpload}
        configParam={configParam}
      />
      <Button
        variant="contained"
        color="error"
        onClick={handleDeletePage}
        disabled={Object.keys(normalizedConfig.pages).length <= 1}
        sx={{ flex: '0 1 auto' }}
      >
        <DeleteOutlineRounded />
      </Button>
      {/*save button */}
      <Button
        sx={{ position: 'fixed', bottom: '20px', right: '20px' }}
        variant="contained"
        color="primary"
        onClick={handleConfigSave}
      >
        Save
      </Button>
      {/* Add page Edit Dialog */}
      <Dialog
        open={isAddPageDialogOpen}
        onClose={() => setIsAddPageDialogOpen(false)}
      >
        <DialogTitle>Add New Page</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Page Name"
            type="text"
            fullWidth
            value={newPageName}
            onChange={(e) => setNewPageName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddPageDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={handleAddPage}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add subpage Dialog */}
      <Dialog
        open={isAddSubpageDialogOpen}
        onClose={() => setIsAddSubpageDialogOpen(false)}
      >
        <DialogTitle>Add New Subpage</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Subpage Name"
            type="text"
            fullWidth
            value={newSubpageName}
            onChange={(e) => setNewSubpageName(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsAddSubpageDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddSubpage}
            disabled={!newSubpageName}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isNavDialogOpen}
        onClose={() => setIsNavDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Navigation</DialogTitle>
        <DialogContent>
          {/* Logo upload section */}
          <Box
            sx={{ mb: 3, border: '1px solid #e0e0e0', p: 2, borderRadius: 1 }}
          >
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Logo Settings
            </Typography>

            {/* Show logo preview if exists */}
            {normalizedConfig.navigationConfig?.logo?.url && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'center' }}>
                <img
                  src={normalizedConfig.navigationConfig.logo.url}
                  alt="Logo preview"
                  style={{ maxHeight: '80px' }}
                />
              </Box>
            )}

            <Button
              variant="contained"
              color="primary"
              onClick={() => setIsLogoFileSelectorOpen(true)}
              sx={{ mb: 2 }}
            >
              Select Logo
            </Button>

            <TextField
              margin="dense"
              label="Logo Alt Text"
              type="text"
              fullWidth
              value={normalizedConfig.navigationConfig?.logo?.alt || ''}
              onChange={(e) =>
                handleNavChange({
                  ...(normalizedConfig.navigationConfig || {}),
                  logo: {
                    ...(normalizedConfig.navigationConfig?.logo || { url: '' }),
                    alt: e.target.value,
                  },
                })
              }
            />

            <TextField
              margin="dense"
              label="Logo Height"
              type="text"
              fullWidth
              value={normalizedConfig.navigationConfig?.logo?.height || '40px'}
              onChange={(e) =>
                handleNavChange({
                  ...(normalizedConfig.navigationConfig || {}),
                  logo: {
                    ...(normalizedConfig.navigationConfig?.logo || {
                      url: '',
                      alt: 'Logo',
                    }),
                    height: e.target.value,
                  },
                })
              }
              helperText="Example: 40px or 3rem"
            />
          </Box>

          {/* Navigation behavior settings */}
          <Box sx={{ border: '1px solid #e0e0e0', p: 2, borderRadius: 1 }}>
            <Typography variant="subtitle1" fontWeight="bold" mb={1}>
              Navigation Behavior
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Typography>Show Home Text: </Typography>
              <Switch
                checked={
                  normalizedConfig.navigationConfig?.showHomeText || false
                }
                onChange={(e) =>
                  handleNavChange({
                    ...(normalizedConfig.navigationConfig || {}),
                    showHomeText: e.target.checked,
                  })
                }
              />
              <Typography sx={{ ml: 2 }}>
                {normalizedConfig.navigationConfig?.showHomeText
                  ? "Showing 'Home' text"
                  : 'Using logo only'}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
              <Typography>Last Item as Button: </Typography>
              <Switch
                checked={
                  normalizedConfig.navigationConfig?.lastItemIsButton || false
                }
                onChange={(e) =>
                  handleNavChange({
                    ...(normalizedConfig.navigationConfig || {}),
                    lastItemIsButton: e.target.checked,
                  })
                }
              />
              <Typography sx={{ ml: 2 }}>
                {normalizedConfig.navigationConfig?.lastItemIsButton
                  ? 'Last item styled as button'
                  : 'All items as text links'}
              </Typography>
            </Box>

            {normalizedConfig.navigationConfig?.lastItemIsButton && (
              <TextField
                margin="dense"
                label="Button Color"
                type="text"
                fullWidth
                value={
                  normalizedConfig.navigationConfig?.buttonColor || 'primary'
                }
                onChange={(e) =>
                  handleNavChange({
                    ...(normalizedConfig.navigationConfig || {}),
                    buttonColor: e.target.value,
                  })
                }
                helperText="Use 'primary', 'secondary', or a hex color like '#FF5733'"
              />
            )}
          </Box>
          <FileSelectorModal
            open={isLogoFileSelectorOpen}
            onClose={() => setIsLogoFileSelectorOpen(false)}
            onSelect={handleLogoFileSelect}
            configParam={configParam}
            handleUpload={handleUpload}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsNavDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsNavDialogOpen(false)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Footer Edit Dialog */}
      <Dialog open={isFooterDialogOpen}>
        <DialogTitle>Edit Footer</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            type="text"
            fullWidth
            value={normalizedConfig.footerConfig?.title || ''}
            onChange={(e) =>
              handleSaveFooter({
                ...normalizedConfig.footerConfig,
                title: e.target.value,
              })
            }
          />
          <TextField
            autoFocus
            margin="dense"
            label="Subtitle"
            type="text"
            fullWidth
            value={normalizedConfig.footerConfig?.subtitle || ''}
            onChange={(e) =>
              handleSaveFooter({
                ...normalizedConfig.footerConfig,
                subtitle: e.target.value,
              })
            }
          />
          <TextField
            autoFocus
            margin="dense"
            label="Instagram"
            type="text"
            fullWidth
            value={normalizedConfig.footerConfig?.links?.instagram || ''}
            onChange={(e) =>
              handleSaveFooter({
                ...normalizedConfig.footerConfig,
                links: {
                  ...normalizedConfig.footerConfig.links,
                  instagram: e.target.value,
                },
              })
            }
          />

          <TextField
            autoFocus
            margin="dense"
            label="Facebook"
            type="text"
            fullWidth
            value={normalizedConfig.footerConfig?.links?.facebook || ''}
            onChange={(e) =>
              handleSaveFooter({
                ...normalizedConfig.footerConfig,
                links: {
                  ...normalizedConfig.footerConfig.links,
                  facebook: e.target.value,
                },
              })
            }
          />

          <TextField
            autoFocus
            margin="dense"
            label="Youtube"
            type="text"
            fullWidth
            value={normalizedConfig.footerConfig?.links?.youtube || ''}
            onChange={(e) =>
              handleSaveFooter({
                ...normalizedConfig.footerConfig,
                links: {
                  ...normalizedConfig.footerConfig.links,
                  youtube: e.target.value,
                },
              })
            }
          />

          <TextField
            autoFocus
            margin="dense"
            label="LinkedIn"
            type="text"
            fullWidth
            value={normalizedConfig.footerConfig?.links?.linkedin || ''}
            onChange={(e) =>
              handleSaveFooter({
                ...normalizedConfig.footerConfig,
                links: {
                  ...normalizedConfig.footerConfig.links,
                  linkedin: e.target.value,
                },
              })
            }
          />

          <TextField
            autoFocus
            margin="dense"
            label="X"
            type="text"
            fullWidth
            value={normalizedConfig.footerConfig?.links?.x || ''}
            onChange={(e) =>
              handleSaveFooter({
                ...normalizedConfig.footerConfig,
                links: {
                  ...normalizedConfig.footerConfig.links,
                  x: e.target.value,
                },
              })
            }
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setIsFooterDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setIsFooterDialogOpen(false)}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Theme Edit Dialog */}
      <Dialog
        open={isThemeDialogOpen}
        onClose={() => setIsThemeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <ThemeEditor
          currentConfig={normalizedConfig}
          handleThemeChange={handleThemeChange}
          onClose={() => setIsThemeDialogOpen(false)}
          configParam={configParam}
          handleUpload={handleUpload}
        />
      </Dialog>

      {/* Rename Page/Subpage Dialog */}
      <Dialog
        open={isRenamePageDialogOpen}
        onClose={() => {
          setIsRenamePageDialogOpen(false);
          setIsRenamingSubpage(false);
          setSubpageToRename('');
          setNewPageKey('');
        }}
      >
        <DialogTitle>
          {isRenamingSubpage ? 'Rename Subpage' : 'Rename Page'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {isRenamingSubpage
              ? `Renaming subpage: ${subpageToRename}`
              : `Renaming page: ${activePage}`}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            label="New Name"
            type="text"
            fullWidth
            value={newPageKey}
            onChange={(e) => setNewPageKey(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsRenamePageDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRenamePage}
            disabled={
              !newPageKey ||
              newPageKey === (isRenamingSubpage ? subpageToRename : activePage)
            }
          >
            Rename
          </Button>
        </DialogActions>
      </Dialog>

      <ReactRouterPrompt when={isDirty}>
        {({ isActive, onConfirm, onCancel }) => (
          <Dialog open={isActive}>
            <div>
              <p>Do you really want to leave?</p>
              <button onClick={onCancel}>Cancel</button>
              <button onClick={onConfirm}>Ok</button>
            </div>
          </Dialog>
        )}
      </ReactRouterPrompt>

      {/* Full JSON Editor Modal */}
      <Dialog
        open={isJsonEditorOpen}
        onClose={() => setIsJsonEditorOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Edit Full Configuration JSON</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            multiline
            value={jsonContent}
            onChange={(e) => setJsonContent(e.target.value)}
            error={!!jsonError}
            helperText={jsonError}
            minRows={25}
            maxRows={40}
            InputProps={{
              style: { fontFamily: 'monospace', fontSize: '0.9rem' },
            }}
            margin="normal"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsJsonEditorOpen(false)} color="primary">
            Cancel
          </Button>
          <Button onClick={handleSaveJson} color="primary" variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Version Control Dialog */}
      <Dialog
        open={isVersionDialogOpen}
        onClose={() => setIsVersionDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Version History</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Button
              variant="outlined"
              onClick={handleSaveCurrentAsVersion}
              startIcon={<History />}
              sx={{ mr: 1 }}
            >
              Save Current as Version
            </Button>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              {configVersions.length} of 10 versions saved
            </Typography>
          </Box>

          {configVersions.length === 0 ? (
            <Typography variant="body1" color="textSecondary">
              No saved versions yet. Click "Save Current as Version" to create
              your first backup.
            </Typography>
          ) : (
            <Box>
              {configVersions.map((version, index) => (
                <Box
                  key={version.id}
                  sx={{
                    p: 2,
                    border: '1px solid #e0e0e0',
                    borderRadius: 1,
                    mb: 1,
                    backgroundColor:
                      selectedVersionId === version.id ? '#f5f5f5' : 'white',
                  }}
                >
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box>
                      <Typography variant="subtitle2">
                        Version {index + 1}: {version.name}
                      </Typography>
                      <Typography variant="caption" color="textSecondary">
                        {new Date(version.timestamp).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Button
                        size="small"
                        variant={
                          selectedVersionId === version.id
                            ? 'contained'
                            : 'outlined'
                        }
                        onClick={() =>
                          setSelectedVersionId(
                            selectedVersionId === version.id ? '' : version.id
                          )
                        }
                        sx={{ mr: 1 }}
                      >
                        {selectedVersionId === version.id
                          ? 'Selected'
                          : 'Select'}
                      </Button>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsVersionDialogOpen(false)} color="primary">
            Close
          </Button>
          {selectedVersionId && (
            <Button
              onClick={handleLoadVersion}
              color="primary"
              variant="contained"
              startIcon={<Restore />}
            >
              Restore Selected Version
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPage;
